import { useParams, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import SectionHero from './components/SectionHero';
import SubcategoryGrid from './components/SubcategoryGrid';
import { catalogProducts, categoriesBySection } from '@/mocks/catalog';
import SeoHead, { SITE_URL } from '@/components/feature/SeoHead';
import { useStaggerReveal, useScrollReveal } from '@/hooks/useScrollReveal';
import { Link } from 'react-router-dom';

// Slug <-> section name mapping
const SLUG_TO_SECTION: Record<string, string> = {
  'lekarstva':             'Лекарства',
  'bady-i-vitaminy':       'БАДы и витамины',
  'dermakosmetika':        'Дермакосметика',
  'gomeopatiya':           'Гомеопатия',
  'kontaktnye-linzy':      'Контактные линзы',
  'medicinskie-izdeliya':  'Медицинские изделия',
  'medicinskie-pribory':   'Медицинские приборы',
};

const SECTION_CONFIG: Record<string, {
  icon: string; color: string; glow: string; bg: string;
  subtitle: string; image: string; totalCount: number;
}> = {
  'Лекарства': {
    icon: 'ri-capsule-line', color: '#ff2d78', glow: 'rgba(255,45,120,0.35)', bg: 'rgba(255,45,120,0.1)',
    subtitle: 'Рецептурные и безрецептурные препараты от ведущих производителей',
    image: 'https://public.readdy.ai/ai/img_res/8a288b15c0606c75605293934144ac4a.jpg',
    totalCount: 10000,
  },
  'БАДы и витамины': {
    icon: 'ri-sun-line', color: '#f0ff00', glow: 'rgba(240,255,0,0.35)', bg: 'rgba(240,255,0,0.1)',
    subtitle: 'Витамины, минералы, омега-3 и спортивное питание',
    image: 'https://readdy.ai/api/search-image?query=vitamins%20supplements%20bottles%20capsules%20colorful%20yellow%20neon%20glow%20dark%20background%20cyberpunk%20pharmacy%20product%20minimal%20clean%20photography%20wide%20banner&width=1200&height=400&seq=hero-vit&orientation=landscape',
    totalCount: 3500,
  },
  'Дермакосметика': {
    icon: 'ri-sparkling-2-line', color: '#ff6b9d', glow: 'rgba(255,107,157,0.35)', bg: 'rgba(255,107,157,0.1)',
    subtitle: 'Профессиональный уход за кожей, волосами и телом',
    image: 'https://readdy.ai/api/search-image?query=dermocosmetics%20skincare%20cream%20serum%20bottles%20tubes%20arranged%20on%20dark%20surface%20pink%20rose%20neon%20glow%20cyberpunk%20aesthetic%20minimal%20clean%20beauty%20photography%20wide%20banner&width=1200&height=400&seq=hero-derm&orientation=landscape',
    totalCount: 2200,
  },
  'Гомеопатия': {
    icon: 'ri-leaf-line', color: '#39ff14', glow: 'rgba(57,255,20,0.35)', bg: 'rgba(57,255,20,0.1)',
    subtitle: 'Натуральные гомеопатические средства для всей семьи',
    image: 'https://readdy.ai/api/search-image?query=homeopathy%20natural%20herbal%20medicine%20small%20bottles%20granules%20green%20neon%20glow%20dark%20background%20cyberpunk%20pharmacy%20product%20minimal%20clean%20photography%20wide%20banner&width=1200&height=400&seq=hero-homeo&orientation=landscape',
    totalCount: 800,
  },
  'Контактные линзы': {
    icon: 'ri-eye-line', color: '#00f5ff', glow: 'rgba(0,245,255,0.35)', bg: 'rgba(0,245,255,0.1)',
    subtitle: 'Линзы, растворы и аксессуары для зрения',
    image: 'https://readdy.ai/api/search-image?query=contact%20lenses%20boxes%20solution%20bottles%20optical%20products%20cyan%20neon%20glow%20dark%20background%20cyberpunk%20pharmacy%20product%20minimal%20clean%20photography%20wide%20banner&width=1200&height=400&seq=hero-lens&orientation=landscape',
    totalCount: 1200,
  },
  'Медицинские изделия': {
    icon: 'ri-first-aid-kit-line', color: '#ff6b00', glow: 'rgba(255,107,0,0.35)', bg: 'rgba(255,107,0,0.1)',
    subtitle: 'Перевязочные материалы, тонометры, термометры и средства защиты',
    image: 'https://readdy.ai/api/search-image?query=medical%20devices%20blood%20pressure%20monitor%20thermometer%20bandages%20medical%20supplies%20orange%20neon%20glow%20dark%20background%20cyberpunk%20pharmacy%20product%20minimal%20clean%20photography%20wide%20banner&width=1200&height=400&seq=hero-mdev&orientation=landscape',
    totalCount: 4000,
  },
  'Медицинские приборы': {
    icon: 'ri-heart-pulse-line', color: '#c77dff', glow: 'rgba(199,125,255,0.35)', bg: 'rgba(199,125,255,0.1)',
    subtitle: 'Небулайзеры, глюкометры, пульсоксиметры и массажёры',
    image: 'https://readdy.ai/api/search-image?query=medical%20equipment%20nebulizer%20glucometer%20pulse%20oximeter%20massager%20devices%20violet%20purple%20neon%20glow%20dark%20background%20cyberpunk%20pharmacy%20product%20minimal%20clean%20photography%20wide%20banner&width=1200&height=400&seq=hero-equip&orientation=landscape',
    totalCount: 900,
  },
};

const CatalogSectionPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const sectionName = SLUG_TO_SECTION[slug ?? ''];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const cfg = sectionName ? SECTION_CONFIG[sectionName] : null;
  const categories = sectionName ? (categoriesBySection[sectionName] ?? []) : [];
  const sectionProducts = sectionName ? catalogProducts.filter(p => p.section === sectionName) : [];
  const hitsProducts = sectionProducts.slice(0, 5);

  // Reveal hooks — must be called unconditionally before any early return
  const { ref: hitsHeaderRef, isVisible: hitsHeaderVisible } = useScrollReveal({ threshold: 0.1 });
  const { containerRef: hitsGridRef, visibleItems: hitsVisible } = useStaggerReveal(hitsProducts.length || 5, { threshold: 0.05 });

  if (!sectionName || !cfg) {
    return <Navigate to="/catalog" replace />;
  }

  const canonicalUrl = `${SITE_URL}/catalog/section/${slug}`;

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${sectionName} — NeonPharma`,
      description: `${cfg.subtitle}. ${sectionProducts.length}+ товаров в разделе ${sectionName}. Быстрая доставка по Москве.`,
      url: canonicalUrl,
      numberOfItems: cfg.totalCount,
      image: cfg.image,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Главная', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Каталог', item: `${SITE_URL}/catalog` },
          { '@type': 'ListItem', position: 3, name: sectionName, item: canonicalUrl },
        ],
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Главная', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Каталог', item: `${SITE_URL}/catalog` },
        { '@type': 'ListItem', position: 3, name: sectionName, item: canonicalUrl },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: `Подкатегории раздела ${sectionName}`,
      numberOfItems: categories.length,
      itemListElement: categories.map((cat, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        name: cat,
        url: `${SITE_URL}/catalog?section=${encodeURIComponent(sectionName)}&category=${encodeURIComponent(cat)}`,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: `Хиты раздела ${sectionName}`,
      numberOfItems: hitsProducts.length,
      itemListElement: hitsProducts.map((p, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        name: p.name,
        url: `${SITE_URL}/product/${p.id}`,
        item: {
          '@type': 'Product',
          name: p.name,
          description: p.description,
          image: p.image,
          brand: { '@type': 'Brand', name: p.brand },
          offers: {
            '@type': 'Offer',
            price: p.price,
            priceCurrency: 'RUB',
            availability: p.inStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
            url: `${SITE_URL}/product/${p.id}`,
          },
        },
      })),
    },
  ];

  return (
    <>
      <SeoHead
        title={`${sectionName} — купить в аптеке NeonPharma | ${cfg.subtitle.split(',')[0]}`}
        description={`${cfg.subtitle}. ${sectionProducts.length}+ товаров в разделе ${sectionName}. Быстрая доставка по Москве за 2 часа. ${categories.slice(0, 3).join(', ')}.`}
        keywords={`${sectionName}, ${categories.slice(0, 4).join(', ')}, купить онлайн, аптека Москва`}
        canonical={`/catalog/section/${slug}`}
        ogImage={cfg.image}
        jsonLd={jsonLd}
      />
      <div className="min-h-screen" style={{ background: '#050508' }}>
        <Navbar />

        <main className="pt-24 pb-20 px-6 lg:px-12">
          <div className="max-w-7xl mx-auto">

            {/* Hero */}
            <SectionHero
              name={sectionName}
              subtitle={cfg.subtitle}
              icon={cfg.icon}
              color={cfg.color}
              glow={cfg.glow}
              bg={cfg.bg}
              image={cfg.image}
              totalCount={cfg.totalCount}
              subcategoryCount={categories.length}
            />

            {/* Subcategory grid */}
            <SubcategoryGrid
              sectionName={sectionName}
              categories={categories}
              color={cfg.color}
              glow={cfg.glow}
              bg={cfg.bg}
            />

            {/* Popular products teaser */}
            <section className="mt-14" aria-label={`Хиты раздела ${sectionName}`}>
              <div
                ref={hitsHeaderRef as React.RefObject<HTMLDivElement>}
                className="flex items-center gap-3 mb-6"
                style={{
                  opacity: hitsHeaderVisible ? 1 : 0,
                  transform: hitsHeaderVisible ? 'translateY(0)' : 'translateY(16px)',
                  transition: 'opacity 0.5s ease, transform 0.5s ease',
                }}
              >
                <span className="font-mono text-xs tracking-[0.2em]" style={{ color: `${cfg.color}80` }}>ПОПУЛЯРНОЕ</span>
                <div className="h-px w-10" style={{ background: `${cfg.color}30` }} />
                <h2 className="font-orbitron font-bold text-xl text-white">Хиты раздела</h2>
              </div>

              <div
                ref={hitsGridRef as React.RefObject<HTMLDivElement>}
                className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4"
              >
                {hitsProducts.map((product, idx) => (
                  <Link
                    key={product.id}
                    to={`/product/${product.id}`}
                    className="group relative rounded-xl overflow-hidden cursor-pointer"
                    style={{
                      background: '#0a0a14',
                      border: `1px solid ${cfg.color}18`,
                      opacity: hitsVisible[idx] ? 1 : 0,
                      transform: hitsVisible[idx] ? 'translateY(0) scale(1)' : 'translateY(24px) scale(0.96)',
                      transition: `opacity 0.4s ease ${idx * 70}ms, transform 0.4s ease ${idx * 70}ms, border-color 0.3s ease, box-shadow 0.3s ease`,
                    }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLElement).style.borderColor = `${cfg.color}50`;
                      (e.currentTarget as HTMLElement).style.boxShadow = `0 0 16px ${cfg.glow}`;
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLElement).style.borderColor = `${cfg.color}18`;
                      (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                    }}
                  >
                    {product.badge && (
                      <div className="absolute top-2 left-2 z-10 font-mono text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ background: product.badgeColor, color: '#050508' }}>
                        {product.badge}
                      </div>
                    )}
                    <div className="w-full h-36 overflow-hidden">
                      <img
                        src={product.image}
                        alt={`${product.name} — купить в NeonPharma`}
                        title={product.name}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-3">
                      <p className="font-mono text-[10px] mb-1 line-clamp-2 leading-snug" style={{ color: 'rgba(255,255,255,0.6)' }}>{product.name}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="font-orbitron font-black text-sm" style={{ color: cfg.color }}>{product.price.toLocaleString('ru-RU')} ₽</span>
                        {product.oldPrice && (
                          <span className="font-mono text-[10px] line-through" style={{ color: 'rgba(255,255,255,0.25)' }}>{product.oldPrice.toLocaleString('ru-RU')} ₽</span>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              <div
                className="mt-6 flex justify-center"
                style={{
                  opacity: hitsVisible[hitsProducts.length - 1] ? 1 : 0,
                  transform: hitsVisible[hitsProducts.length - 1] ? 'translateY(0)' : 'translateY(12px)',
                  transition: `opacity 0.4s ease ${hitsProducts.length * 70 + 80}ms, transform 0.4s ease ${hitsProducts.length * 70 + 80}ms`,
                }}
              >
                <Link
                  to={`/catalog?section=${encodeURIComponent(sectionName)}`}
                  className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-3 rounded-full font-mono text-xs tracking-widest cursor-pointer transition-all duration-300"
                  style={{ background: `${cfg.color}12`, border: `1px solid ${cfg.color}40`, color: cfg.color }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = `0 0 20px ${cfg.glow}`; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = 'none'; }}
                >
                  СМОТРЕТЬ ВСЕ ТОВАРЫ РАЗДЕЛА <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </section>

          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default CatalogSectionPage;
